import type { ActivitySubjectRecommendation, ActivitySubjectCard } from './types/activity-feed.ts';
import type { AccountThreadLightView, ThreadTopicSummary } from './types/thread-work.ts';
import type { WorkActionPresentation, WorkActionCommand, WorkActionContext } from './types/work-action.ts';

type SourceAction = Pick<ActivitySubjectRecommendation,
  'id' | 'label' | 'summary' | 'state' | 'action' | 'sourceRunId' | 'sourceActionId' | 'continuationPlanningStatus'>;
type Subject = Pick<ActivitySubjectCard, 'subjectId' | 'subjectKey' | 'subjectKind' | 'subjectLabel'>;

const topicRunCommand = (topic: ThreadTopicSummary, subjectId: string): WorkActionCommand | null => {
  const run = topic.activeActionRun;
  if (!run) return null;
  const label = topic.visibleState === 'awaiting_review' ? 'Reprendre la validation' : 'Reprendre le travail';
  if (run.taskActionRunId) {
    const query = new URLSearchParams({
      topic: topic.id,
      result: run.taskActionRunId,
    });
    return {
      kind: 'resume',
      label,
      href: `/threads/${encodeURIComponent(subjectId)}?${query.toString()}`,
    };
  }
  if (run.flowExecutionId) return { kind: 'resume', label, href: `/executions/${encodeURIComponent(run.flowExecutionId)}` };
  if (run.forgeConversationId) return { kind: 'resume', label: 'Reprendre la conception', href: `/forge?conversationId=${encodeURIComponent(run.forgeConversationId)}` };
  return null;
};

/** Server projection; no semantic routing or label matching. Also used by the
 * bounded compatibility adapter while an older server is still running. */
export const projectWorkActions = (params: {
  subject: Subject;
  threadState?: AccountThreadLightView | null;
  recommendations: SourceAction[];
  observedAt?: string;
}): WorkActionPresentation[] => {
  const { subject, threadState, recommendations } = params;
  const observedAt = Date.parse(params.observedAt ?? new Date().toISOString());
  const topics = threadState?.currentTopic
    ? [threadState.currentTopic, ...threadState.otherTopics.filter(t => t.id !== threadState.currentTopic?.id)]
    : [];
  const entries: Array<{ topic: ThreadTopicSummary | null; source: SourceAction | null }> = topics.length
    ? topics.map(topic => ({ topic, source: recommendations.find(r => topic.sourceActionId !== null && r.sourceActionId === topic.sourceActionId && (topic.sourceRunId === null || r.sourceRunId === topic.sourceRunId)) ?? null }))
    : recommendations.filter(r => r.state === 'open' || r.state === 'weak_covered' || r.state === 'exact_triggered').map(source => ({ source, topic: null }));

  return entries.map(({ topic, source }) => {
    const proposedTitle = topic?.proposal?.actionTitle;
    const title = proposedTitle ?? topic?.title ?? source?.label ?? 'Travail demandé';
    const context: WorkActionContext = {
      ...subject, title, preparation: topic?.preparation ?? null,
      resultId: topic?.activeActionRun?.taskActionRunId ?? undefined,
      threadId: threadState?.threadId ?? null,
      topicId: threadState?.source === 'persistent' ? topic?.id ?? null : null,
      topicVersion: threadState?.source === 'persistent' ? topic?.version ?? null : null,
      sourceRunId: topic?.sourceRunId ?? source?.sourceRunId ?? null,
      sourceActionId: topic?.sourceActionId ?? source?.sourceActionId ?? null,
      decisionFocus: threadState?.source === 'persistent' && topic?.decision && threadState.threadId ? {
        accountSubjectId: threadState.accountSubjectId, subjectId: subject.subjectId,
        accountLabel: subject.subjectLabel, threadId: threadState.threadId,
        topicId: topic.id, topicTitle: title, decisionId: topic.decision.id,
        actionId: topic.sourceActionId, version: topic.version, sourceRunId: topic.sourceRunId,
        rationale: topic.decision.reason, evidenceRefs: topic.decision.evidenceRefs,
      } : null,
    };
    const state = topic?.visibleState ?? (source?.state === 'exact_triggered' ? 'running' : 'actionable');
    const health = threadState?.decisionHealth === 'stale' ? 'stale' : topic?.decision?.health ?? threadState?.decisionHealth;
    let notice: string | null = null;
    let command: WorkActionCommand = { kind: 'work', label: 'Préparer avec Domino' };
    const resume = topic ? topicRunCommand(topic, subject.subjectId) : null;
    if (threadState?.decisionHealthReasonCode === 'read_model_unavailable') {
      notice = 'Les actions ne peuvent pas être rechargées. Réessayez dans quelques instants.';
      command = { kind: 'none' };
    } else if (topic?.activeActionRun?.runtimeKind === 'task_action' && topic.proposal && (topic.activeActionRun.status === 'running' || topic.activeActionRun.status === 'queued')) command = { kind: 'task_proposal', proposalId: topic.proposal.id, topicId: topic.id, version: topic.version, label: 'Reprendre le travail' };
    else if (resume) command = resume;
    else if (state === 'closed' || state === 'waiting' || state === 'running' || topic?.activeWait) command = { kind: 'none' };
    else if (topic?.preparation?.status === 'queued' || topic?.preparation?.status === 'running') {
      notice = topic.preparation.message ? `${topic.preparation.message} Une reprise est prévue.` : 'Préparation en cours…';
      command = { kind: 'none' };
    } else if (topic?.preparation?.status === 'failed') {
      notice = topic.preparation.message ?? 'La préparation n’a pas abouti. Votre demande est conservée.';
      command = { kind: 'refresh', label: 'Reprendre la préparation', retry: true };
    } else if (!topic?.decision && source?.continuationPlanningStatus === 'failed' && context.sourceRunId) {
      notice = 'La préparation a échoué. Le résultat reste disponible.';
      command = { kind: 'prepare_plan', sourceRunId: context.sourceRunId, label: 'Reprendre la préparation', retry: true };
    } else if (source?.continuationPlanningStatus === 'running' || source?.continuationPlanningStatus === 'pending' || health === 'refreshing') {
      notice = 'Préparation en cours…'; command = { kind: 'none' };
    } else if (health === 'stale' || health === 'unavailable' || topic?.proposal?.requiresPreparation
      || topic?.proposal?.expiresAt && Date.parse(topic.proposal.expiresAt) <= observedAt) {
      notice = threadState?.decisionHealthReasonCode === 'objective_changed'
        ? 'L’objectif a changé. Actualisez la proposition avant de poursuivre.'
        : 'Le contexte de cette proposition doit être vérifié avant de poursuivre.';
      command = { kind: context.topicId ? 'refresh' : 'work', label: 'Actualiser la proposition' };
    } else if (threadState?.source !== 'persistent' && source?.action && state === 'actionable' && context.sourceRunId) {
      // Legacy plans must be attached to the canonical Thread before launching.
      command = { kind: 'prepare_plan', sourceRunId: context.sourceRunId, label: 'Préparer cette action' };
    } else if (topic?.proposal?.status === 'active' && (topic.proposal.executorKind === 'recipe' || topic.proposal.executorKind === 'capability')) {
      command = { kind: 'task_proposal', proposalId: topic.proposal.id, topicId: topic.id, version: topic.version, label: topic.proposal.ctaLabel };
    }
    else if (topic?.proposal?.status === 'active' && topic.proposal.executorKind === 'tool') {
      command = { kind: 'tool_proposal', toolKey: topic.proposal.executorKey, proposalId: topic.proposal.id, topicId: topic.id, version: topic.version, label: topic.proposal.ctaLabel, prefill: topic.proposal.prefill };
    } else if (topic?.proposal?.status === 'active' && topic.proposal.executorKind === 'forge') {
      command = { kind: 'forge', proposalId: topic.proposal.id, topicId: topic.id, version: topic.version, label: 'Concevoir une application' };
    } else if (source?.continuationPlanningStatus === 'unavailable') {
      notice = 'Aucune action automatique ne couvre encore cette demande. Vous pouvez préciser le travail à préparer.';
    }
    return {
      id: topic?.id ?? source?.id ?? subject.subjectId, context, title,
      reason: title === 'Travail demandé'
        ? topic?.title ?? source?.label ?? null
        : topic?.proposal?.rationale ?? source?.summary ??
          (topic?.stateReason && !/^[a-z]+(?:_[a-z]+)+$/.test(topic.stateReason)
            ? topic.stateReason
            : null),
      evidenceRefs: topic?.decision?.evidenceRefs ?? [], dueAt: topic?.dueAt ?? null,
      state, notice, command,
    };
  });
};
